import { useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import type { Contract } from '@traceio-org/platform-contracts';
import { AdminProblem } from '../../components/AdminProblem';
import { SavedNotice } from '../../components/feedback';
import { SelectField } from '../../components/fields';
import { useDialogTarget } from '../../components/WriteDialog';
import { useAdmin, useStaff } from '../../context';
import { useAdminRead } from '../../useAdminRead';
import { previousCursors } from '../OrganizationsPage';
import { AdjustmentsSection } from './billing/AdjustmentsSection';
import { ChangeContractDialog, ContractsSection, EndTrialDialog } from './billing/ContractsSection';
import { DocumentsSection } from './billing/DocumentsSection';
import { LineItemsSection } from './billing/LineItemsSection';
import { SignContractDialog } from './billing/SignContractDialog';
import {
  billingTabSearch,
  contractName,
  defaultContract,
  periodOptions,
  type BillingView,
} from './billing/shared';
import { UsageSection } from './billing/UsageSection';
import { carriedState } from './OrganizationLayout';
import { ExemptionDialog, ExemptionSection } from './billing/ExemptionSection';
import { useOrganizationScope } from './scope';

type BillingDialog =
  | { kind: 'sign' }
  | { kind: 'change'; contract: Contract }
  | { kind: 'endTrial'; contract: Contract }
  | { kind: 'exemption' };

/**
 * Billing (/organizations/:id/billing): the organization's contracts, and for one of them (the one in force by
 * default, or the one the address names) and one of its periods (the current one by default), the usage and the
 * line items; then the adjustments and the provider's invoices. Staff with staff.billing.read see everything;
 * staff.billing.manage signs and changes contracts and requests or decides adjustments. Billing is the commercial
 * layer, like entitlements: it needs no support access grant, and platform-api audits every read.
 */
export function BillingTab() {
  const { organization, abilities, reloadOrganization } = useOrganizationScope();
  const { api } = useAdmin();
  const { person } = useStaff();
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const organizationId = organization.id;
  const contractParam = params.get('contract') || undefined;
  const periodParam = params.get('period') || undefined;
  const eventsCursor = params.get('events') || undefined;
  const previous = eventsCursor ? previousCursors(location.state) : [];
  const backSearch = carriedState(location.state)?.listSearch;
  const now = Date.now();

  const key = abilities.readBilling ? `contracts\u0000${organizationId}` : null;
  const read = useAdminRead(key, (signal) => api.listContracts(organizationId, signal));
  const contracts = read.dataKey === key && !read.failure ? (read.data?.items ?? null) : null;
  const selected = contracts
    ? (contracts.find((c) => c.id === contractParam) ?? defaultContract(contracts))
    : null;

  const [notice, setNotice] = useState<string | null>(null);
  const dialog = useDialogTarget<BillingDialog>();
  // The contract being changed as last read, so a conflict's re-read reaches the open dialog.
  const target = dialog.target;
  const changing =
    target?.kind === 'change'
      ? (contracts?.find((c) => c.id === target.contract.id) ?? target.contract)
      : null;
  const endingTrial =
    target?.kind === 'endTrial'
      ? (contracts?.find((c) => c.id === target.contract.id) ?? target.contract)
      : null;

  const go = (view: BillingView, previousOfView: string[] | null) =>
    void navigate(
      { search: billingTabSearch(view) },
      {
        replace: previousOfView === null,
        state: {
          ...(backSearch !== undefined ? { listSearch: backSearch } : {}),
          ...(previousOfView ? { previous: previousOfView } : {}),
        },
      },
    );

  if (!abilities.readBilling) {
    return <AdminProblem failure={{ kind: 'forbidden' }} subject="billing" />;
  }

  const saved = (message: string) => {
    setNotice(message);
    read.retry();
  };

  return (
    <>
      <SavedNotice message={notice} />
      <ExemptionSection
        organization={organization}
        canManage={abilities.manageBilling}
        onChange={() => dialog.show({ kind: 'exemption' })}
      />
      <ContractsSection
        read={read}
        contracts={contracts}
        canManage={abilities.manageBilling}
        onSign={() => dialog.show({ kind: 'sign' })}
        onChange={(contract) => dialog.show({ kind: 'change', contract })}
        onEndTrial={(contract) => dialog.show({ kind: 'endTrial', contract })}
      />
      {contracts && contracts.length > 0 && selected ? (
        <>
          <div className="admin-filters admin-filters--billing">
            {contracts.length > 1 ? (
              <SelectField
                label="Contract"
                value={selected.id}
                options={contracts.map((c) => ({ value: c.id, label: contractName(c) }))}
                onChange={(value) => go({ contract: value }, null)}
                hint="The usage and line items below are this contract's."
              />
            ) : null}
            <SelectField
              label="Period"
              value={periodParam ?? ''}
              options={periodOptions(selected, now)}
              onChange={(value) => go({ contract: contractParam, period: value || undefined }, null)}
            />
          </div>
          <UsageSection
            organizationId={organizationId}
            contract={selected}
            periodStart={periodParam}
            cursor={eventsCursor}
            previous={previous}
            onPage={(cursor, previousOfPage) =>
              go({ contract: contractParam, period: periodParam, events: cursor }, previousOfPage)
            }
          />
          <LineItemsSection organizationId={organizationId} contract={selected} periodStart={periodParam} />
        </>
      ) : null}
      {contracts ? (
        <AdjustmentsSection
          organizationId={organizationId}
          contracts={contracts}
          selected={selected}
          canManage={abilities.manageBilling}
          staffUserId={person.userId}
          onNotice={setNotice}
        />
      ) : null}
      <DocumentsSection organizationId={organizationId} />
      {abilities.manageBilling && dialog.target?.kind === 'exemption' ? (
        <ExemptionDialog
          key={dialog.key}
          organization={organization}
          open={dialog.open}
          onClose={dialog.close}
          onSaved={(message) => {
            setNotice(message);
            reloadOrganization();
          }}
        />
      ) : null}
      {abilities.manageBilling && dialog.target?.kind === 'sign' ? (
        <SignContractDialog
          key={dialog.key}
          organization={organization}
          open={dialog.open}
          onClose={dialog.close}
          onSaved={(contract) => saved(`The ${contractName(contract)} contract is signed.`)}
        />
      ) : null}
      {abilities.manageBilling && changing ? (
        <ChangeContractDialog
          key={dialog.key}
          organizationId={organizationId}
          contract={changing}
          open={dialog.open}
          onClose={dialog.close}
          onSaved={saved}
          onConflict={read.retry}
        />
      ) : null}
      {abilities.manageBilling && endingTrial ? (
        <EndTrialDialog
          key={dialog.key}
          organizationId={organizationId}
          contract={endingTrial}
          open={dialog.open}
          onClose={dialog.close}
          onSaved={saved}
          onConflict={read.retry}
        />
      ) : null}
    </>
  );
}
